import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { getLabelDice } from '../utils/dice';

const TOAST_ID = 'lastDiceRoll';

// Jets pris en compte pour le toast (du point de vue du spectateur) :
//  - jamais les "mises à jour" d'inventaire/PV ;
//  - un jet caché ne notifie que son auteur et le MJ, pas les autres joueurs.
const isToastableRoll = (roll, campaign, user) =>
  roll && roll.diceType !== 'update'
  && (!roll.isHided || roll.userUid === user.uid || campaign.idUserDm === user.uid);

const countRealRolls = (list, campaign, user) =>
  list.filter((roll) => isToastableRoll(roll, campaign, user)).length;

// Quand le chat est masqué, affiche uniquement le DERNIER jet en toast.
// Un seul toast est utilisé : chaque nouveau jet remplace le précédent
// (pas une pile de toasts).
//
// Deux pièges évités :
//  - On ne compte que les VRAIS jets (hors "mise à jour" d'inventaire/PV),
//    sinon toute modif d'inventaire ferait grossir la liste et notifierait.
//  - Le premier instantané reçu de la base sert de référence : on ne notifie
//    jamais l'historique déjà présent au chargement de la page. On distingue
//    le rendu initial (état useState) du premier instantané via le changement
//    de référence de `list` (setRollList crée toujours un nouveau tableau).
export function useRollToasts(list, enabled, campaign, user) {
  const baseline = useRef(null);
  const seenList = useRef(null);

  useEffect(() => {
    // Ré-exécution due à un changement de enabled/campaign/user (pas de `list`) :
    // rien à faire, on ne traite que les vrais changements de liste.
    if (list === seenList.current) return;
    const prevList = seenList.current;
    seenList.current = list;

    // Rendu initial (état useState par défaut), les données ne sont pas encore là.
    if (prevList === null) return;

    const realCount = countRealRolls(list, campaign, user);

    // Premier instantané reçu de la base : sert de référence, sans notifier.
    if (baseline.current === null) {
      baseline.current = realCount;
      return;
    }

    if (enabled && realCount > baseline.current) {
      // dernier jet notifiable pour ce spectateur (hors "mise à jour",
      // et jets cachés seulement si auteur/MJ)
      const latest = [...list].reverse().find((roll) => isToastableRoll(roll, campaign, user));
      if (latest) {
        const message = `🎲 ${latest.userName} · ${getLabelDice(latest, campaign, user)} → ${latest.value}`;
        if (toast.isActive(TOAST_ID)) {
          toast.update(TOAST_ID, { render: message, autoClose: 4000 });
        } else {
          toast.info(message, { toastId: TOAST_ID, autoClose: 4000 });
        }
      }
    }

    baseline.current = realCount;
  }, [list, enabled, campaign, user]);
}
